/**
 * Client-side port of the backend's typed-retype comparison (ADR-049's
 * diacritic fold, applied to `check_retype_answer` in
 * `backend/app/services/learning_service.py`).
 *
 * ADR-086 Decision 2 keeps retype validation entirely client-side and asserts
 * "same acceptance rule" as the server. Before this file, `useLearningSession`
 * graded the retype with a hand-rolled `s.trim().toLowerCase().replace(...)`
 * that folded neither diacritics nor Unicode-aware edge punctuation — a term
 * carrying an accented character (e.g. "vis-à-vis") could not be retyped from
 * an ASCII keyboard, contradicting the parity Decision 2 claims. See
 * docs/diagnostics/adr-049-hyphenated-diacritic-answer-mis-grade-2026-08-31.md.
 *
 * Parity is enforced structurally, not by convention: both this
 * implementation and the Python one are asserted against the same vector
 * table at `test-fixtures/adr049-retype-diacritic-vectors.json` (frontend:
 * `answerNormalize.test.ts`; backend: `test_learning.py::TestCheckRetypeAnswerVectors`).
 * A change to either side that the shared vectors don't also cover is the
 * drift this pairing exists to catch.
 *
 * The ADR-049 amendment of 2026-09-01 widened the fold by exactly three
 * classes — apostrophe variants, dash variants, internal whitespace runs —
 * and named this file as part of the surface it governs, precisely because
 * the client/server parity ADR-086 Decision 2 asserts was found to be false
 * once already. Anything beyond those three (stripping internal punctuation
 * generally, so that "co-op" would equal "coop") is still out of scope and
 * still needs its own decision.
 */

// Unicode-aware "strip at the edges" class. JS's `\W` stays ASCII-only even
// with the `u` flag, so a literal port of the Python regex would treat a
// trailing accented letter as punctuation and strip it — the diagnostic
// measured this exact false-accept ("café" -> "caf"). This class instead
// names what to KEEP (letters/marks/digits from any script) and strips
// everything else found at either edge.
const EDGE_NON_WORD = /^[^\p{L}\p{N}\p{M}]+|[^\p{L}\p{N}\p{M}]+$/gu

function stripEdgePunctuation(s: string): string {
  return s.replace(EDGE_NON_WORD, '')
}

// ADR-049 amendment (2026-09-01) — typographic variant folding, three classes
// only. Mirrors `_TYPOGRAPHIC_MAP` / `fold_typography` in
// `backend/app/utils/answer_normalize.py` character for character. Characters
// are *normalised*, never removed: "co-op" still does not equal "coop".
const APOSTROPHE_VARIANTS = /[‘’ʼʹ′´`＇]/g
const DASH_VARIANTS = /[‐‑‒–—―−﹘﹣－]/g
const WHITESPACE_RUN = /\s+/g

/** Fold apostrophe and dash variants to ASCII; collapse internal whitespace
 * runs to one space. Mirrors `fold_typography` in the backend module. */
export function foldTypography(s: string): string {
  return s
    .replace(APOSTROPHE_VARIANTS, "'")
    .replace(DASH_VARIANTS, '-')
    .replace(WHITESPACE_RUN, ' ')
}

/** NFD-decompose and drop combining marks (Unicode category Mn). Mirrors
 * `fold_diacritics` in `backend/app/utils/answer_normalize.py` step for step:
 * café→cafe, naïve→naive, schön→schon. */
export function foldDiacritics(s: string): string {
  return s.normalize('NFD').replace(/\p{Mn}/gu, '')
}

/** Case-, whitespace-, edge-punctuation- and diacritic-insensitive comparison
 * key. Mirrors `fold_answer` — same three steps, same order. Not currently
 * called from the retype gate (which needs `checkRetypeAnswer`'s short/numeric
 * bypass instead) but kept as the direct analogue for any future client
 * comparison that should match `check_answer` rather than `check_retype_answer`. */
export function foldAnswer(s: string): string {
  return foldDiacritics(stripEdgePunctuation(foldTypography(s.trim().toLowerCase())))
}

const NUMERIC_RE = /^[\d.,+\-/]+$/

/**
 * Client port of `check_retype_answer` — the mandatory retype step after a
 * wrong typed answer. Exact-match only, no fuzzy scoring.
 *
 * Mirrors the backend's short/numeric bypass: for a correct term of length
 * ≤3 or one that is purely numeric, edge-punctuation stripping is skipped
 * (stripping a single non-word term like "+" would normalize it to "" and
 * accept any input). Diacritic and typographic folding always apply on both
 * paths; which branch is taken is decided on the raw lowercased term before
 * either fold, exactly as the backend does it.
 */
export function checkRetypeAnswer(userAnswer: string, correctTerm: string): boolean {
  let ua = userAnswer.trim().toLowerCase()
  let ct = correctTerm.trim().toLowerCase()

  const isShort = ct.length <= 3
  const isNumeric = NUMERIC_RE.test(ct)
  if (isShort || isNumeric) {
    return foldDiacritics(foldTypography(ua)) === foldDiacritics(foldTypography(ct))
  }

  ua = stripEdgePunctuation(foldTypography(ua))
  ct = stripEdgePunctuation(foldTypography(ct))
  return foldDiacritics(ua) === foldDiacritics(ct)
}
